<template>
  <div class="play-by-play-grid" v-if="isDataValid">
    <table>
      <thead>
        <tr>
          <th class="col-narrow">Point No.</th>
          <!-- <th>Serve/Receive</th> -->
          <th>Serve</th>
          <th>Receive</th>
          <th>Attacks</th>
          <th>Blocks</th>
          <th>Digs</th>
          <th class="col-narrow">Outcome</th>
          <th>How</th>
          <th>Who</th>
          <th class="col-narrow">P#</th>
          <th class="col-narrow">Rotation</th>
          <th class="col-narrow">Off-court</th>
          <th class="col-narrow">Score</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in reversedPlayByPlay" :key="index">
          <td class="col-narrow">{{ item.id }}</td>
          <!-- <td>{{ item.startPoint }}</td> -->
          <td>
            <span v-if="item.serveAction.player && item.serveAction.player.number" class="player-number">{{ item.serveAction.player.number }}</span>
            {{ item.serveAction.player && item.serveAction.player.name ? item.serveAction.player.name : '' }}
            <template v-if="item.serveAction.call">
              <span class="entry-badge">X</span>
            </template>
            <template v-else-if="item.serveAction.rating !== null && item.serveAction.rating !== undefined">
              <span class="entry-badge" :class="getServeRatingClass(item.serveAction.rating)">
                {{ item.serveAction.rating }}
              </span>
            </template>
          </td>
          <td>
            <template v-if="item.receiveAction.call">
              {{ item.receiveAction.call }}
            </template>
            <template v-else>
              <span v-if="item.receiveAction.player && item.receiveAction.player.number" class="player-number">{{ item.receiveAction.player.number }}</span>
              <template v-if="item.receiveAction.player === 'Team receive error'">Team</template>
              <template v-else>{{ item.receiveAction.player && item.receiveAction.player.name ? item.receiveAction.player.name : '' }}</template>
              <template v-if="item.receiveAction.rating !== null && item.receiveAction.rating !== undefined">
                <span class="entry-badge" :class="getReceiveRatingClass(item.receiveAction.rating)">
                  {{ item.receiveAction.rating }}
                </span>
              </template>
            </template>
          </td>
          <td>
            <div class="count-list" v-if="attackEntries(item).length">
              <div v-for="entry in attackEntries(item)" :key="`a-${item.id}-${entry.number}`" class="count-row">
                <div class="count-player">
                  <span class="player-number">{{ entry.number }}</span>
                  <span class="count-name">{{ entry && entry.name ? entry.name : '' }}</span>
                </div>
                <span class="count-value">{{ entry.countLabel }}</span>
              </div>
            </div>
            <span v-else>-</span>
          </td>
          <td>
            <div class="count-list" v-if="blockEntries(item).length">
              <div v-for="entry in blockEntries(item)" :key="`b-${item.id}-${entry.number}`" class="count-row">
                <div class="count-player">
                  <span class="player-number">{{ entry.number }}</span>
                  <span class="count-name">{{ entry && entry.name ? entry.name : '' }}</span>
                </div>
                <span class="count-value">{{ entry.countLabel }}</span>
              </div>
            </div>
            <span v-else>-</span>
          </td>
          <td>
            <div class="count-list" v-if="digEntries(item).length">
              <div v-for="entry in digEntries(item)" :key="`d-${item.id}-${entry.number}`" class="count-row">
                <div class="count-player">
                  <span class="player-number">{{ entry.number }}</span>
                  <span class="count-name">{{ entry && entry.name ? entry.name : '' }}</span>
                </div>
                <span class="count-value">{{ entry.countLabel }}</span>
              </div>
            </div>
            <span v-else>-</span>
          </td>
          <td class="col-narrow" :class="getOutcomeClass(item.scoreAction.outcome)"><span class="point-outcome">{{ item.scoreAction.outcome }}</span></td>
          <td>
            <template v-if="item.scoreAction.outcome">
              {{ item.scoreAction.pointDetail }}
            </template>
          </td>
          <td>
            <template v-if="item.scoreAction.outcome === 'Won'">
              <template v-if="item.scoreAction.pointDetail === 'Kill block (assist)'">
                <span
                  v-if="item.scoreAction.player && item.scoreAction.player.number"
                  class="player-number"
                >
                  {{ item.scoreAction.player.number }}
                </span>
                {{ item.scoreAction.player && item.scoreAction.player.name ? item.scoreAction.player.name : '' }}
                <span v-if="item.scoreAction.secondPlayer"> / </span>
                <span v-if="item.scoreAction.secondPlayer && item.scoreAction.secondPlayer.number" class="player-number">
                  {{ item.scoreAction.secondPlayer.number }}
                </span>
                {{ item.scoreAction.secondPlayer && item.scoreAction.secondPlayer.name ? item.scoreAction.secondPlayer.name : '' }}
              </template>
              <template v-else>
                <strong v-if="item.scoreAction.player === 'Team'">Team</strong>
                <span
                  v-else-if="item.scoreAction.player && item.scoreAction.player.number"
                  class="player-number"
                >
                  {{ item.scoreAction.player.number }}
                </span>
                {{ item.scoreAction.player && item.scoreAction.player.name ? item.scoreAction.player.name : '' }}
              </template>
            </template>
            <template v-else-if="item.scoreAction.outcome === 'Lost'">
              <strong v-if="item.scoreAction.player === 'Team'">Team</strong>
              <span
                v-else-if="item.scoreAction.player && item.scoreAction.player.number"
                class="player-number"
              >
                {{ item.scoreAction.player.number }}
              </span>
              {{ item.scoreAction.player && item.scoreAction.player.name ? item.scoreAction.player.name : '' }}
            </template>
          </td>
          <td class="col-narrow">{{ item.currentRotationCode }}</td>
          <td class="col-narrow">
            <div class="rotation-grid-mini">
              <span
                v-for="position in getRotationGridPositions(item.currentOnCourtNumbers)"
                :key="position.position"
                :class="{
                  'setter-circle': isSetter(position.number),
                  'front-row': isFrontRow(position.position),
                  'libero-circle': isLiberoOnCourt(position.number, item)
                }"
              >
                {{ position.number }}
              </span>
            </div>
          </td>
          <td class="col-narrow">
            <span
              v-if="getOffCourtNumber(item)"
              class="off-court-circle"
              :class="{ 'libero-off-court': isOffCourtLibero(item) }"
            >
              {{ getOffCourtNumber(item) }}
            </span>
            <span v-else>X</span>
          </td>
          <td class="col-narrow">
            <span :class="{ 'score-won': item.scoreAction.outcome === 'Won' }">{{ item.currentScore.won }}</span> -
            <span :class="{ 'score-lost': item.scoreAction.outcome === 'Lost' }">{{ item.currentScore.lost }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'PlayByPlayGrid',
  props: {
    playByPlay: {
      type: Array,
      required: true
    },
    currentLineup: {
      type: Array,
      required: true
    }
  },
  methods: {
    isValidRow(item) {
      if (!item) return false;

      // Check required action objects exist
      if (!item.serveAction || !item.receiveAction || !item.scoreAction) return false;

      // Ensure player objects exist (can be empty objects {} but not null/undefined)
      if (item.serveAction.player === null || item.serveAction.player === undefined) return false;
      if (item.receiveAction.player === null || item.receiveAction.player === undefined) return false;

      // Check that if player has a name property, it's not null
      if (item.serveAction.player && item.serveAction.player.name === null) return false;
      if (item.receiveAction.player && item.receiveAction.player.name === null) return false;
      if (item.scoreAction.player && item.scoreAction.player.name === null) return false;
      if (item.scoreAction.secondPlayer && item.scoreAction.secondPlayer.name === null) return false;

      return true;
    },
    attackEntries(point) {
      if (!point) return [];
      return this.getCountEntries(point, point.duringRally?.attacks || {});
    },
    blockEntries(point) {
      if (!point) return [];
      return this.getBlockEntries(
        point,
        point.duringRally?.blocksSolo || {},
        point.duringRally?.blocksJoint || {}
      );
    },
    digEntries(point) {
      if (!point) return [];
      return this.getCountEntries(point, point.duringRally?.digs || {});
    },
    getCountEntries(point, bucket = {}) {
      return Object.entries(bucket)
        .map(([number, count]) => ({ number: Number(number), count }))
        .filter(item => item.count > 0)
        .sort((a, b) => a.number - b.number)
        .map(item => ({
          ...item,
          name: this.getPlayerName(point, item.number),
          countLabel: this.formatCountValue(item.count)
        }))
        .filter(item => item.name !== null && item.name !== undefined);
    },
    getBlockEntries(point, solo = {}, joint = {}) {
      const merged = { ...solo };
      Object.entries(joint).forEach(([number, count]) => {
        merged[number] = (merged[number] || 0) + count * 0.5;
      });
      return this.getCountEntries(point, merged);
    },
    formatCountValue(value) {
      return Number.isInteger(value) ? value : value.toFixed(1);
    },
    getPlayerName(point, number) {
      if (!point || !number) {
        return '';
      }
      const rosterMap = point.duringRallyRoster || {};
      if (rosterMap[number]) {
        return rosterMap[number] || '';
      }
      if (!Array.isArray(this.currentLineup)) {
        return '';
      }
      const found = this.currentLineup.find(item => item && item.player && item.player.number === number);
      return found && found.player && found.player.name ? found.player.name : '';
    },
    getRotationGridPositions(positions) {
      if (!Array.isArray(positions) || positions.length < 6) {
        return [];
      }
      const gridOrder = [4, 3, 2, 5, 6, 1];
      return gridOrder.map(position => ({
        position,
        number: positions[position - 1]
      }));
    },
    isFrontRow(positionId) {
      return [2, 3, 4].includes(positionId);
    },
    isLiberoOnCourt(number, item) {
      return item.currentLiberoNumber && number === item.currentLiberoNumber;
    },
    getOffCourtNumber(item) {
      if (!item || !item.currentLiberoNumber) {
        return '';
      }
      const onCourt = item.currentOnCourtNumbers || [];
      const base = item.currentLineupNumbers || [];
      const liberoIndex = onCourt.findIndex(number => number === item.currentLiberoNumber);
      if (liberoIndex !== -1 && base[liberoIndex]) {
        return base[liberoIndex];
      }
      return item.currentLiberoNumber;
    },
    isOffCourtLibero(item) {
      if (!item || !item.currentLiberoNumber) {
        return false;
      }
      const onCourt = item.currentOnCourtNumbers || [];
      return !onCourt.includes(item.currentLiberoNumber);
    },
    getServeRatingClass(rating) {
      if (rating === 0) {
        return 'badge-error';
      }
      if (rating === 4) {
        return 'badge-success';
      }
      return '';
    },
    getReceiveRatingClass(rating) {
      if (rating === 0) {
        return 'badge-error';
      }
      if (rating === 3) {
        return 'badge-success';
      }
      if (rating === 1) {
        return 'badge-warning';
      }
      return '';
    },
    getOutcomeClass(outcome) {
      return {
        'outcome-won': outcome === 'Won',
        'outcome-lost': outcome === 'Lost',
      };
    },
    isSetter(number) {
      if (!this.currentLineup) {
        return false;
      }
      const setterByRoleCode = this.currentLineup.find(pos => pos && pos.roleCode === 'S');
      if (setterByRoleCode && setterByRoleCode.player) {
        return setterByRoleCode.player.number === number;
      }
      const setter = this.currentLineup.find(pos => pos.player && pos.player.role === 'setter');
      return setter && setter.player && setter.player.number === number;
    }
  },
  computed: {
    isDataValid() {
      return Array.isArray(this.playByPlay) && Array.isArray(this.currentLineup);
    },
    reversedPlayByPlay() {
      if (!Array.isArray(this.playByPlay)) {
        return [];
      }
      return [...this.playByPlay]
        .filter(item => this.isValidRow(item))
        .reverse();
    }
  }
};
</script>

<style scoped>
.play-by-play-grid {
  overflow: auto;
  margin: 0 20px;
  color: var(--grey-900);
}

.count-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.count-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.count-player {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.count-name {
  font-weight: 500;
}

.count-value {
  font-weight: 700;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  width: max-content;
  min-width: 100%;
}

th, td {
  border: 1px solid var(--grey-50);
  padding: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: wrap;
  width: 10%;
  min-width: 80px;

}

th.col-narrow,
td.col-narrow {
  width: 5%;
}

.entry-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--grey-20);
  color: var(--grey-800);
  font-size: 12px;
  font-weight: 700;
  padding: 0 6px;
  margin-left: 6px;
}

.entry-badge.badge-error {
  background: var(--red-50);
  color: var(--red-600);
}

.entry-badge.badge-success {
  background: var(--green-100);
  color: var(--green-700);
}

.entry-badge.badge-warning {
  background: var(--grey-20);
  color: var(--grey-600);
}

th {
  background-color: var(--grey-15);
  white-space: nowrap;
}

.setter-circle {
  background-color: var(--grey-800);
  border-color: var(--grey-800);
  color: var(--white);
}

.outcome-won,
.outcome-lost {
  span {
    padding: 12px 20px;
    border-radius: 4px;
  }
}

.outcome-won {
  .point-outcome {
    background: var(--green-500);
    color: var(--white);
  }
}

.outcome-lost {
  .point-outcome {
    background: var(--red-300);
    color: var(--white);
  }
}

.rotation-grid-mini {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2px;
  justify-items: center;
  align-items: center;
  width: fit-content;
  margin: 0 auto;
}

.rotation-grid-mini span {
  display: inline-flex;
  width: 18px;
  height: 18px;
  border: 1px solid var(--grey-50);
  border-radius: 40px;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 900;
  margin: 0;
  padding: 0;

  &.setter-circle {
    background-color: var(--grey-800);
    border-color: var(--grey-800);
    color: var(--white);
  }
}

.rotation-grid-mini span.front-row {
  border-width: 1px;
  border-color: var(--grey-800);
}

.rotation-grid-mini span.libero-circle {
  border: 1px solid var(--red-400);
  color: var(--red-400);
}

.off-court-circle {
  display: inline-flex;
  width: 18px;
  height: 18px;
  border: 1px solid var(--grey-50);
  border-radius: 40px;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 900;
}

.off-court-circle.libero-off-court {
  border: 1px solid var(--red-400);
  color: var(--red-400);
}
</style>